import { Controller } from '@hotwired/stimulus';

// Client-side search across the FAQ questions/answers rendered on the page:
// matching questions are expanded, scrolled into view and highlighted;
// non-matching ones (and now-empty categories) are hidden.
export default class extends Controller {
  static targets = ['input', 'item', 'category', 'status', 'emptyState'];

  static values = {
    noResults: String,
    resultFoundTemplate: String,
    resultsFoundTemplate: String,
  };

  search() {
    const query = this.inputTarget.value.trim().toLowerCase();

    this.clearHighlights();

    if (query === '') {
      this.reset();
      return;
    }

    let firstMatch = null;
    let matchCount = 0;

    this.itemTargets.forEach((item) => {
      const matches =
        (item.dataset.searchTitle || '').includes(query) ||
        (item.dataset.searchAnswer || '').includes(query);

      item.classList.toggle('hidden', !matches);

      if (matches) {
        matchCount += 1;
        this.expand(item);
        this.highlight(item, query);
        firstMatch ||= item;
      }
    });

    this.categoryTargets.forEach((category) => {
      const hasVisibleItem = Array.from(
        category.querySelectorAll('[data-faq-search-target="item"]')
      ).some((item) => !item.classList.contains('hidden'));

      category.classList.toggle('hidden', !hasVisibleItem);
    });

    this.toggleEmptyState(matchCount === 0);
    firstMatch?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.announce(matchCount);
  }

  clear(event) {
    if (event.key !== 'Escape') return;

    this.inputTarget.value = '';
    this.search();
  }

  reset() {
    this.itemTargets.forEach((item) => item.classList.remove('hidden'));
    this.categoryTargets.forEach((category) => category.classList.remove('hidden'));
    this.toggleEmptyState(false);
    this.announce(null);
  }

  toggleEmptyState(visible) {
    if (!this.hasEmptyStateTarget) return;

    this.emptyStateTarget.classList.toggle('hidden', !visible);
  }

  expand(item) {
    const button = item.querySelector('[data-accordion-target="button"]');
    const content = item.querySelector('[data-accordion-target="content"]');
    const icon = item.querySelector('[data-accordion-target="icon"]');

    content?.classList.remove('hidden');
    icon?.classList.add('rotate-180');
    button?.setAttribute('aria-expanded', 'true');
  }

  highlight(item, query) {
    this.highlightText(item.querySelector('[data-faq-search-target="title"]'), query);
    this.highlightText(item.querySelector('[data-faq-search-target="answer"]'), query);
  }

  highlightText(root, query) {
    if (!root) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node;

    // eslint-disable-next-line no-cond-assign
    while ((node = walker.nextNode())) textNodes.push(node);

    textNodes.forEach((textNode) => {
      const text = textNode.textContent;
      const index = text.toLowerCase().indexOf(query);
      if (index === -1) return;

      const mark = document.createElement('mark');
      mark.className = 'bg-warning-100 text-dark rounded-xs';
      mark.dataset.faqSearchHighlight = 'true';
      mark.textContent = text.slice(index, index + query.length);

      const fragment = document.createDocumentFragment();
      fragment.append(
        document.createTextNode(text.slice(0, index)),
        mark,
        document.createTextNode(text.slice(index + query.length))
      );

      textNode.replaceWith(fragment);
    });
  }

  clearHighlights() {
    this.element.querySelectorAll('[data-faq-search-highlight]').forEach((mark) => {
      mark.replaceWith(document.createTextNode(mark.textContent));
    });
  }

  announce(matchCount) {
    if (!this.hasStatusTarget) return;

    if (matchCount === null) {
      this.statusTarget.textContent = '';
    } else if (matchCount === 0) {
      this.statusTarget.textContent = this.noResultsValue;
    } else if (matchCount === 1) {
      this.statusTarget.textContent = this.resultFoundTemplateValue.replace('%{count}', matchCount);
    } else {
      this.statusTarget.textContent = this.resultsFoundTemplateValue.replace('%{count}', matchCount);
    }
  }
}
